import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Helper to get full image URL for uploaded images
// Converts /api/uploads/xxx.png to http://localhost:3003/api/uploads/xxx.png
export const getImageUrl = (path) => {
  if (!path) return '';
  // Already a full URL
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  // Uploaded image path - prepend backend URL
  if (path.startsWith('/api/uploads/')) {
    return `${API_URL}${path}`;
  }
  // Other paths (like /images/logo.png) - keep as-is for local public folder
  return path;
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_URL}/api/admin/auth/refresh`, {
            refresh_token: refreshToken
          });
          
          const { access_token, refresh_token } = response.data;
          localStorage.setItem('access_token', access_token);
          localStorage.setItem('refresh_token', refresh_token);
          
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        } catch (refreshError) {
          // Refresh failed, logout
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          window.location.href = '/admin/login';
          return Promise.reject(refreshError);
        }
      }
    }
    
    return Promise.reject(error);
  }
);

// Public API
export const publicApi = {
  getTherapies: () => api.get('/therapies?active_only=true'),
  getTherapy: (id) => api.get(`/therapies/${id}`),
  getPrices: (therapyId) => api.get(`/prices?active_only=true${therapyId ? `&therapy_id=${therapyId}` : ''}`),
  getAffiliations: () => api.get('/affiliations?active_only=true'),
  getPolicies: () => api.get('/policies?active_only=true'),
  getPolicy: (slug) => api.get(`/policies/slug/${slug}`),
  getSettings: () => api.get('/settings'),
  submitContact: (data) => api.post('/contact', data),
  
  // Bookings
  getBookingSettings: () => api.get('/bookings/settings'),
  getAvailableDates: (priceId, month, year) => api.get(`/bookings/available-dates?price_id=${priceId}&month=${month}&year=${year}`),
  getAvailability: (date, priceId) => api.get(`/bookings/availability?date=${date}&price_id=${priceId}`),
  createBooking: (data) => api.post('/bookings/create', data),
  cancelBooking: (id) => api.post(`/bookings/${id}/cancel`),

  // Payments
  createCheckout: (bookingId) => api.post('/payments/checkout', { booking_id: bookingId }),
  getPaymentStatus: (bookingId) => api.get(`/payments/status/${bookingId}`)
};

// Admin API
export const adminApi = {
  // Auth
  login: (username, password) => api.post('/admin/auth/login', { username, password }),
  refresh: (refreshToken) => api.post('/admin/auth/refresh', { refresh_token: refreshToken }),
  getMe: () => api.get('/admin/auth/me'),
  
  // Admin Users
  getUsers: () => api.get('/admin/users'),
  getUser: (id) => api.get(`/admin/users/${id}`),
  createUser: (data) => api.post('/admin/users', data),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  
  // Therapies
  getTherapies: () => api.get('/therapies'),
  createTherapy: (data) => api.post('/admin/therapies', data),
  updateTherapy: (id, data) => api.put(`/admin/therapies/${id}`, data),
  deleteTherapy: (id) => api.delete(`/admin/therapies/${id}`),
  
  // Prices
  getPrices: (therapyId) => api.get(`/prices${therapyId ? `?therapy_id=${therapyId}` : ''}`),
  createPrice: (data) => api.post('/admin/prices', data),
  updatePrice: (id, data) => api.put(`/admin/prices/${id}`, data),
  deletePrice: (id) => api.delete(`/admin/prices/${id}`),
  
  // Contacts
  getContacts: (unreadOnly) => api.get(`/admin/contacts${unreadOnly ? '?unread_only=true' : ''}`),
  getContact: (id) => api.get(`/admin/contacts/${id}`),
  markContactRead: (id) => api.put(`/admin/contacts/${id}/read`),
  updateContactNotes: (id, notes) => api.put(`/admin/contacts/${id}/notes`, { notes }),
  deleteContact: (id) => api.delete(`/admin/contacts/${id}`),
  
  // Affiliations
  getAffiliations: () => api.get('/affiliations'),
  createAffiliation: (data) => api.post('/admin/affiliations', data),
  updateAffiliation: (id, data) => api.put(`/admin/affiliations/${id}`, data),
  deleteAffiliation: (id) => api.delete(`/admin/affiliations/${id}`),
  
  // Policies
  getPolicies: () => api.get('/policies'),
  createPolicy: (data) => api.post('/admin/policies', data),
  updatePolicy: (id, data) => api.put(`/admin/policies/${id}`, data),
  deletePolicy: (id) => api.delete(`/admin/policies/${id}`),
  
  // Settings
  getSettings: () => api.get('/settings'),
  updateSettings: (data) => api.put('/admin/settings', data),
  
  // Clients
  getClients: (search) => api.get(`/admin/clients${search ? `?search=${search}` : ''}`),
  getClient: (id) => api.get(`/admin/clients/${id}`),
  createClient: (data) => api.post('/admin/clients', data),
  updateClient: (id, data) => api.put(`/admin/clients/${id}`, data),
  deleteClient: (id) => api.delete(`/admin/clients/${id}`),
  
  // Client Notes
  getClientNotes: (clientId) => api.get(`/admin/clients/${clientId}/notes`),
  createClientNote: (clientId, data) => api.post(`/admin/clients/${clientId}/notes`, data),
  updateClientNote: (clientId, noteId, data) => api.put(`/admin/clients/${clientId}/notes/${noteId}`, data),
  deleteClientNote: (clientId, noteId) => api.delete(`/admin/clients/${clientId}/notes/${noteId}`),
  
  // Client Consultations
  getConsultations: (clientId) => api.get(`/admin/clients/${clientId}/consultations`),
  getConsultation: (clientId, consultationId) => api.get(`/admin/clients/${clientId}/consultations/${consultationId}`),
  createConsultation: (clientId, data) => api.post(`/admin/clients/${clientId}/consultations`, data),
  updateConsultation: (clientId, consultationId, data) => api.put(`/admin/clients/${clientId}/consultations/${consultationId}`, data),
  deleteConsultation: (clientId, consultationId) => api.delete(`/admin/clients/${clientId}/consultations/${consultationId}`),
  
  // Image Upload
  uploadImage: (formData) => api.post('/admin/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteImage: (filename) => api.delete(`/admin/upload/${filename}`),
  
  // Bookings
  getBookings: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/admin/bookings${queryString ? `?${queryString}` : ''}`);
  },
  getBooking: (id) => api.get(`/admin/bookings/${id}`),
  getBookingsCalendar: (month, year) => api.get(`/admin/bookings/calendar?month=${month}&year=${year}`),
  getUpcomingBookings: (days = 7) => api.get(`/admin/bookings/upcoming?days=${days}`),
  createBooking: (data) => api.post('/admin/bookings/create', data),
  updateBooking: (id, data) => api.put(`/admin/bookings/${id}`, data),
  updateBookingStatus: (id, status) => api.put(`/admin/bookings/${id}/status`, { status }),
  deleteBooking: (id) => api.delete(`/admin/bookings/${id}`),

  // Blocked Times
  getBlockedTimes: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/admin/blocked-times${queryString ? `?${queryString}` : ''}`);
  },
  getBlockedTimesForDate: (date) => api.get(`/admin/blocked-times/for-date/${date}`),
  createBlockedTime: (data) => api.post('/admin/blocked-times', data),
  updateBlockedTime: (id, data) => api.put(`/admin/blocked-times/${id}`, data),
  deleteBlockedTime: (id) => api.delete(`/admin/blocked-times/${id}`),

  // Google Calendar
  getGoogleCalendarStatus: () => api.get('/admin/google-calendar/status'),
  getGoogleCalendarConfig: () => api.get('/admin/google-calendar/config'),
  saveGoogleCalendarConfig: (config) => api.post('/admin/google-calendar/config', config),
  getGoogleCalendarAuthUrl: () => api.get('/admin/google-calendar/auth-url'),
  disconnectGoogleCalendar: () => api.post('/admin/google-calendar/disconnect'),
  testGoogleCalendar: () => api.post('/admin/google-calendar/test'),

  // SumUp Payments
  getSumUpConfig: () => api.get('/admin/payments/sumup/config'),
  saveSumUpConfig: (config) => api.post('/admin/payments/sumup/config', config),
  testSumUp: () => api.post('/admin/payments/sumup/test'),
  disconnectSumUp: () => api.post('/admin/payments/sumup/disconnect'),

  // Gift Vouchers
  getVouchers: (params = {}) => {
    const queryString = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v))).toString();
    return api.get(`/admin/vouchers${queryString ? `?${queryString}` : ''}`);
  },
  createVoucher: (data) => api.post('/admin/vouchers', data),
  updateVoucher: (id, data) => api.put(`/admin/vouchers/${id}`, data),
  redeemVoucher: (id, data) => api.put(`/admin/vouchers/${id}/redeem`, data),
  deleteVoucher: (id) => api.delete(`/admin/vouchers/${id}`)
};

export default api;
